<template>
  <v-menu
    :close-on-content-click="false"
    offset-y
    left
    transition="slide-y-transition"
    style=""
    v-model="menuOpen"
    @input="onMenuToggle"
  >
    <template #activator="{ on }">
      <v-btn
        text
        :icon="$vuetify.breakpoint.smAndDown"
        v-on="on"
        v-show="hasHotelAssigned"
        :class="{'btn-40': $vuetify.breakpoint.smAndDown}"
      >
        <span
          class="text-truncate d-md-inline d-sm-none"
          style="max-width: 200px"
          v-show="$vuetify.breakpoint.mdAndUp"
          :class="[($vuetify.rtl ? 'mr-1' : 'ml-1') + (IsDemoHotel(selectedHotel) ? ' red--text' : '')]"
          v-if="!isLoadingHotel"
        >{{ selectedHotel.name }}</span>
        <span
          class="text-truncate d-md-inline d-sm-none"
          style="max-width: 200px; color: #999; font-style: italic;"
          v-show="$vuetify.breakpoint.mdAndUp"
          :class="[$vuetify.rtl ? 'mr-1' : 'ml-1']"
          v-else
        >Loading...</span>
        <v-icon
          class="d-sm-inline d-md-none"
        >
          mdi-domain
        </v-icon>
        <v-icon
          right
          class="d-md-inline d-sm-none"
          v-show="$vuetify.breakpoint.mdAndUp"
        >
          mdi-chevron-down
        </v-icon>
      </v-btn>
    </template>
    <v-card
      max-height="500px"
      width="400px"
      style="
        background: white;
        padding: 10px 0;
        border: solid 1px #d6d0d0 !important;
      "
    >
      <v-text-field
        v-if="showSearch"
        v-model="searchKeyword"
        placeholder="Filter hotel..."
        flat
        hide-details
        rounded
        prepend-inner-icon="mdi-magnify"
        class="mx-1 mb-1"
        ref="hotelSearchField"
      />
      <v-list
        dense
        nav
        style="max-height: 400px; overflow: auto"
      >
        <v-list-item-group
          :value="activeList"
          color="primary"
        >
          <v-list-item
            v-for="hotel in filteredHotels"
            :key="hotel.id"
            @click="setHotel(hotel)"
          >
            <v-list-item-title>
              <v-icon
                v-if="hotel.default_hotel"
                style="color: darkorange; font-size: small; top: -2px;"
              >
                mdi-star
              </v-icon>
              <span :class="IsDemoHotel(hotel) ? 'red--text' : ''">{{ hotel.name }}</span>
            </v-list-item-title>
          </v-list-item>
        </v-list-item-group>
      </v-list>
    </v-card>
  </v-menu>
</template>

<script lang="ts">
import qs from "query-string";
import { Component, Vue, Watch } from "vue-property-decorator";
import Auth from "@/services/Auth";
import { Api } from "@/services/Api";
import { autoFocusMixin } from "@/mixins/autoFocusMixin";

@Component({
  mixins: [autoFocusMixin]
})
export default class ToolbarHotels extends Vue {
  hasHotelAssigned: boolean = false;
  searchKeyword: string = "";
  menuOpen: boolean = false;

  get openToolbarMenu() {
    return this.$store.state.app.openToolbarMenu;
  }

  async mounted() {
    await this.getMyHotels();
  }

  async getMyHotels() {
    await this.$store.dispatch("app/getMyHotels");
    if (this.selectedHotel?.id) {
      await this.$store.dispatch("app/getMyOutlets", { hotelId: this.selectedHotel.id });
    }
  }
  get activeList() {
    return this.filteredHotels.findIndex(el => el.id === this.selectedHotel?.id) ?? undefined;
  }
  get showSearch() {
    return this.hotels && this.hotels.length > 0;  // Standardized: Always show search when hotels exist
  }
  get state() {
    return this.$store.state.app;
  }
  get hotels() {
    const hotels = this.state.hotels;
    this.hasHotelAssigned = hotels && hotels.length > 0;
    return hotels;
  }
  get isLoadingHotel() {
    // Show loading when a chain is selected but current hotel doesn't belong to it
    const selectedChainId = this.$store.state.app.selectedHotelChainId;

    // If "All Chains" is selected, not loading
    if (selectedChainId === null) return false;

    // If we have a current hotel
    const currentHotel = this.selectedHotel;
    if (!currentHotel || !currentHotel.id) return false;

    // Check if current hotel belongs to the selected chain
    // If it doesn't, we're in a transition state (about to reload)
    return currentHotel.hotel_chain_id !== selectedChainId;
  }

  get filteredHotels() {
    if (!this.hotels) return [];

    let filteredByChain = this.hotels;

    // Filter by selected hotel chain if one is selected
    const selectedChainId = this.$store.state.app.selectedHotelChainId;
    if (selectedChainId !== null) {
      filteredByChain = this.hotels.filter((hotel) => {
        return hotel.hotel_chain_id === selectedChainId;
      });
    }

    // Then filter by search keyword
    return filteredByChain.filter((hotel) => {
      return hotel?.name
        ?.toLowerCase()
        ?.includes(this.searchKeyword?.toLowerCase());
    });
  }
  get hotel() {
    return this.state.hotel;
  }
  get selectedHotel() {
    let hotel: any = {};
    if (this.hotels && this.hotels.length > 0) {
      const hotelId = this.$route.query.hotelId;
      // Find default Hotel
      hotel = this.hotels.find((i) => i.default_hotel);
      if (hotel == null || typeof hotel == "undefined"){
        hotel = this.hotels[0];
      }
      if (hotelId) {
        hotel = this.hotels.find((i) => i.id == hotelId);
      }
    }
    if (!this.$route.query.hotelId && hotel.id) {
      this.setHotel(hotel);
    } else {
      this.setSelectedHotel(hotel);
    }
    return hotel || {};
  }

  async autoAssignOutlet(hotel) {
    const token = await Auth.GetTokenPayload();
    const outletsResponse = await Api.get(`/my/outlets/${token?.uid}/v1?hotelId=${hotel.id}`, null, { maxAgeInSecond: 0 });
    const outlets: any[] = outletsResponse.data.Data || [];
    let autoAssignOutlet: boolean = false;
    const query: any = this.$route.query;
    query.hotelId = hotel.id + "";
    // check if there is outlet assigned in query string
    if (outlets.length > 0 && (typeof query.outletId == "undefined" || query.outletId == "")) {
      autoAssignOutlet = true;
    } else if (outlets.filter(r => r.id == query.outlet).length == 0) {
      autoAssignOutlet = true;
    }

    if (autoAssignOutlet) {
      // Find default outlet
      const outlet = outlets.find(r => r.default_outlet == true);
      if (typeof outlet != "undefined") {
        query.outletId = outlet.id + "";
      } else {
        if (typeof outlets[0] != "undefined") {
          query.outletId = outlets[0].id + "";
        } else {
          // means that hotel doesn't have any outlet
          delete query.outletId;
        }
      }
    }

    return query;
  }

  IsDemoHotel(hotel){
    return hotel.id <= 100;
  }
  async setHotel(hotel) {
    const query: any = await this.autoAssignOutlet(hotel);
    const location: any = this.$route.path + "?" + qs.stringify(query);
    window.location.href = location;
  }
  setSelectedHotel(hotel) {
    this.$store.commit("app/setSelectedHotel", hotel);
  }

  onMenuToggle(isOpen: boolean) {
    (this as any).focusSearchOnMenuOpen(isOpen, this.showSearch, "hotelSearchField");

    // Update store when this menu opens/closes
    if (isOpen) {
      this.$store.commit("app/setOpenToolbarMenu", "hotels");
    } else if (this.openToolbarMenu === "hotels") {
      this.$store.commit("app/setOpenToolbarMenu", null);
    }
  }

  @Watch("openToolbarMenu")
  onOpenToolbarMenuChange(newMenu: string | null) {
    // Close this menu if another menu opens
    if (newMenu && newMenu !== "hotels") {
      this.menuOpen = false;
    }
  }
}
</script>
